import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import { GoogleAuth } from 'google-auth-library';

export interface GoogleServiceAccountConfig {
	email: string;
	keyFile: string;
}

export interface GoogleAuthOptions {
	keyFile?: string;
	scopes: string[];
	subject?: string;
}

export class GoogleAuthManager {
	private auth: GoogleAuth;
	private keyFilePath?: string;

	constructor(options: GoogleAuthOptions) {
		this.auth = new GoogleAuth({
			keyFile: options.keyFile,
			scopes: options.scopes,
		});
		this.keyFilePath = options.keyFile;
	}

	async getAccessToken(): Promise<string> {
		const client = await this.auth.getClient();
		const tokenResponse = await client.getAccessToken();
		if (!tokenResponse.token) {
			throw new Error('Failed to get access token');
		}
		return tokenResponse.token;
	}

	async getAuthenticatedClient() {
		return this.auth.getClient();
	}

	async getServiceAccountEmail(): Promise<string | null> {
		try {
			// Try to get email from the key file
			if (this.keyFilePath) {
				const keyData = loadServiceAccountKey(this.keyFilePath);
				return keyData.client_email || null;
			}
			// Fallback: try to get from the auth client
			const client = await this.auth.getClient();
			// @ts-ignore - accessing private property
			return client.email || client.clientEmail || null;
		} catch {
			return null;
		}
	}
}

export function getDefaultKeyPath(filename = 'gtm-api-automation.json'): string {
	// First try config directory relative to current working directory
	const configPath = path.join(process.cwd(), 'config', filename);
	if (fs.existsSync(configPath)) {
		return configPath;
	}
	
	// Fallback to home directory for backward compatibility  
	return path.join(os.homedir(), filename);
}

export function validateKeyFile(keyPath: string): boolean {
	try {
		if (!fs.existsSync(keyPath)) {
			return false;
		}
		const keyContent = fs.readFileSync(keyPath, 'utf8');
		const keyData = JSON.parse(keyContent);
		return keyData.type === 'service_account' && !!keyData.private_key;
	} catch {
		return false;
	}
}

export function loadServiceAccountKey(keyPath: string): any {
	if (!validateKeyFile(keyPath)) {
		throw new Error(`Invalid service account key file at ${keyPath}`);
	}
	return JSON.parse(fs.readFileSync(keyPath, 'utf8'));
}
